/**
 * EditListingPage — full-page edit screen for an existing marketplace
 * listing. Replaces the old "publish dialog in edit mode" UX.
 *
 * Layout:
 *   * Page breadcrumbs
 *   * Hero header (title + delist + view-on-marketplace links)
 *   * Two cards stacked:
 *       1. Details (ListingDetailsForm)
 *       2. Pricing (PricingForm)
 *   * Sticky save bar at the bottom (Save / Cancel)
 *
 * Wired to:
 *   * GET  /marketplace/listings/:id          (hydrate)
 *   * PATCH /marketplace/listings/:id         (save)
 *   * DELETE /marketplace/listings/:id        (delist)
 */

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Stack, Typography, alpha, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BlockIcon from '@mui/icons-material/Block';
import SaveIcon from '@mui/icons-material/Save';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import { CMButton, CMButtonOutlined } from '../../containers/styledComponents';

import { fetchRequest as fetchCategories } from '../../redux/actions/marketplace/categories';
import {
  fetchListing, updateListing, deleteListing, clearMutations,
} from '../../redux/actions/marketplace/listings';

import ListingDetailsForm from './ListingDetailsForm';
import PricingForm from './PricingForm';


const EMPTY_FORM = {
  signal_key:            '',
  title:                 '',
  description:           '',
  icon_url:              '',
  source_label:          '',
  sla:                   '',
  update_frequency:      '',
  data_interval:         '',
  kind:                  'both',
  tags:                  [],
  category:              '',
  pricing_model:         'free',
  price_monthly_credits: 0,
  price_onetime_credits: 0,
};


const EditListingPage = ({
  fetchCategories, fetchListing, updateListing, deleteListing,
  clearMutations,
  categories, listingResp, listingLoading, listingError, mutations,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { listingId } = useParams();

  const [form, setForm] = useState(EMPTY_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [delistOpen, setDelistOpen] = useState(false);

  // Track the original snapshot so the "Cancel" button can revert
  // and so the Save button is a no-op when nothing actually changed.
  const [original, setOriginal] = useState(EMPTY_FORM);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMany = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    fetchCategories();
    if (listingId) fetchListing(listingId);
    return () => { clearMutations(); };
  }, [fetchCategories, fetchListing, listingId, clearMutations]);

  // Hydrate the form once the BE response lands.
  useEffect(() => {
    if (hydrated) return;
    const l = listingResp?.listing;
    if (!l) return;
    const seeded = {
      signal_key:            l.signal_key || '',
      title:                 l.title || l.signal_key || '',
      description:           l.description || '',
      icon_url:              l.icon_url || '',
      source_label:          l.source_label || '',
      sla:                   l.sla || '',
      update_frequency:      l.update_frequency || '',
      data_interval:         l.data_interval || '',
      kind:                  l.kind || 'both',
      tags:                  Array.isArray(l.tags) ? l.tags : [],
      // Listings still allow multi-category in the BE schema, but the
      // edit / publish wizard now treats them as single-pick. Take
      // the first stored slug as the active category.
      category:              (l.categories || [])[0] || '',
      visibility:            l.visibility || 'public',
      org_uid:               l.org_uid || null,
      price_monthly_credits: Number(l.price_monthly_credits) || 0,
      price_onetime_credits: Number(l.price_onetime_credits) || 0,
    };
    setForm(seeded);
    setOriginal(seeded);
    setHydrated(true);
  }, [listingResp, hydrated]);

  // After a successful update, refresh the BE snapshot so the
  // "dirty" tracker resets — and stay on the page so the user can
  // keep editing.
  useEffect(() => {
    if (mutations.action === 'update' && mutations.result
        && !mutations.isLoading && !mutations.error) {
      setOriginal(form);
      clearMutations();
    }
  }, [mutations, clearMutations, form]);

  // Hard-delist: BE returns 200 → bounce back to the seller's
  // monetization page.
  useEffect(() => {
    if (mutations.action === 'delete' && mutations.result
        && !mutations.isLoading && !mutations.error) {
      clearMutations();
      navigate('/settings/monetization/published');
    }
  }, [mutations, clearMutations, navigate]);

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(original),
    [form, original],
  );

  const canSave = !!form.category && !!form.title?.trim();

  const handleSave = () => {
    const body = {
      title:                 form.title.trim(),
      description:           (form.description || '').trim(),
      icon_url:              (form.icon_url || '').trim() || null,
      source_label:          (form.source_label || '').trim(),
      sla:                   (form.sla || '').trim(),
      update_frequency:      (form.update_frequency || '').trim(),
      data_interval:         (form.data_interval || '').trim(),
      kind:                  form.kind || 'both',
      tags:                  form.tags || [],
      categories:            form.category ? [form.category] : [],
      price_monthly_credits: Number(form.price_monthly_credits) || 0,
      price_onetime_credits: Number(form.price_onetime_credits) || 0,
    };
    updateListing(listingId, body);
  };

  // ----- render -----

  if (listingLoading && !listingResp) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (listingError && !listingResp) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
        <PageBreadcrumbs items={[
          { text: 'Monetization', link: '/settings/monetization' },
          { text: 'Edit listing' },
        ]} />
        <Alert severity="error" sx={{ mt: 2 }}>
          {String(listingError)}
        </Alert>
      </Box>
    );
  }

  const listing = listingResp?.listing;
  if (!listing) return null;

  const subscribers = listing.subscribers_count || 0;
  const canDelist = subscribers === 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto', pb: 12 }}>
      <PageBreadcrumbs items={[
        { text: 'Monetization', link: '/settings/monetization' },
        { text: 'Published signals', link: '/settings/monetization/published' },
        { text: listing.title || listing.signal_key || 'Edit listing' },
      ]} />

      {/* ── Hero header ── */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
             alignItems={{ md: 'center' }} justifyContent="space-between"
             sx={{ mt: 2, mb: 2.5 }}>
        <Box>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
            <CMButtonOutlined
              startIcon={<ArrowBackIcon />} size="small"
              onClick={() => navigate('/settings/monetization/published')}
            >
              Back
            </CMButtonOutlined>
            <Typography variant="h5" fontWeight={700}
                        sx={{ letterSpacing: -0.3 }}>
              {form.title || listing.signal_key}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {subscribers === 0
              ? 'No active subscribers yet'
              : `${subscribers} active subscriber${subscribers === 1 ? '' : 's'}`}
            {' · '}
            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
              {listing.signal_key}
            </Box>
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text" size="small"
            startIcon={<OpenInNewIcon fontSize="small" />}
            onClick={() => navigate(`/marketplace/listings/${listingId}`)}
          >
            View on marketplace
          </Button>
          <Button
            variant="text" size="small" color="error"
            startIcon={<BlockIcon fontSize="small" />}
            disabled={!canDelist}
            onClick={() => setDelistOpen(true)}
          >
            Delist
          </Button>
        </Stack>
      </Stack>

      {/* ── Details card ── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="overline"
                      sx={{ fontWeight: 700, letterSpacing: 0.5, fontSize: 10.5,
                            color: 'text.secondary', display: 'block', mb: 1 }}>
            Listing details
          </Typography>
          <ListingDetailsForm
            form={form}
            set={set}
            setMany={setMany}
            categories={categories?.categories || []}
            signalKey={listing.signal_key}
            disableSignalKey
          />
        </CardContent>
      </Card>

      {/* ── Pricing card ── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="overline"
                      sx={{ fontWeight: 700, letterSpacing: 0.5, fontSize: 10.5,
                            color: 'text.secondary', display: 'block', mb: 1 }}>
            Pricing
          </Typography>
          {form.visibility === 'org' ? (
            <Alert severity="success"
                   sx={{ borderRadius: 1.25, py: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: 12 }}>
                <strong>Free for organization members.</strong>{' '}
                Org-scoped listings don't have pricing — every member of
                your org subscribes at no cost.
              </Typography>
            </Alert>
          ) : (
            <>
              {!canDelist && (
                <Alert severity="info" sx={{ mb: 1.25, borderRadius: 1.25, py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: 12 }}>
                    Active subscribers — pricing changes only affect{' '}
                    <strong>new</strong> subscriptions.
                  </Typography>
                </Alert>
              )}
              <PricingForm form={form} set={set} setMany={setMany} />
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Sticky save bar ── */}
      <Box sx={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        p: 1.5,
        bgcolor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        boxShadow: `0 -4px 12px ${alpha(theme.palette.text.primary, 0.04)}`,
        zIndex: 10,
      }}>
        <Box sx={{
          maxWidth: 1100, mx: 'auto',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Typography variant="caption"
                      color={isDirty ? 'warning.main' : 'text.secondary'}
                      sx={{ flex: 1, fontWeight: 600 }}>
            {isDirty ? 'You have unsaved changes' : 'No unsaved changes'}
          </Typography>
          <CMButtonOutlined
            disabled={!isDirty || mutations.isLoading}
            onClick={() => setForm(original)}
          >
            Discard
          </CMButtonOutlined>
          <CMButton
            startIcon={mutations.isLoading && mutations.action === 'update'
              ? <CircularProgress size={14} />
              : <SaveIcon fontSize="small" />}
            disabled={!isDirty || !canSave
              || (mutations.isLoading && mutations.action === 'update')}
            onClick={handleSave}
          >
            Save changes
          </CMButton>
        </Box>
      </Box>

      {mutations.error && (
        <Alert severity="error" sx={{ position: 'sticky', bottom: 80, mt: 2 }}>
          {String(mutations.error)}
        </Alert>
      )}

      {/* ── Delist confirm ── */}
      <Dialog open={delistOpen} onClose={() => setDelistOpen(false)}
              maxWidth="xs" fullWidth>
        <DialogTitle>Delist this signal?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes <strong>{listing.title || listing.signal_key}</strong>{' '}
            from the marketplace. Buyers will no longer see it. You can
            re-publish it later from the Data Signals page. Earnings
            history is preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDelistOpen(false)}>Cancel</Button>
          <CMButton
            color="error"
            onClick={() => {
              setDelistOpen(false);
              deleteListing(listingId);
            }}
          >
            Delist
          </CMButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


const mapStateToProps = (state) => ({
  categories:     state.marketplace_categories.response,
  listingResp:    state.marketplace_detail.response,
  listingLoading: state.marketplace_detail.isLoading,
  listingError:   state.marketplace_detail.error,
  mutations:      state.marketplace_mutations,
});

const mapDispatchToProps = (dispatch) => ({
  fetchCategories: () => dispatch(fetchCategories()),
  fetchListing:    (id) => dispatch(fetchListing(id)),
  updateListing:   (id, body) => dispatch(updateListing(id, body)),
  deleteListing:   (id) => dispatch(deleteListing(id)),
  clearMutations:  () => dispatch(clearMutations()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditListingPage);
